<template>
  <!--
    Shopify's Badge. A pill carries the label, led by a dot in the label's own colour, or by an icon
    when the caller names one.

    The pill only takes colour when the state wants somebody to act. "Paid" and "Completed" have
    settled, so they sit on the grey pill. "Pending" and "Overdue" take the colour, because a merchant
    scanning a table should see the rows that need them and nothing else.

    The dot is `bg-current`, so it follows the pill's text colour and needs no class map of its own.

    `motion-safe:` on the pulse, so it stops for a reader who asked the OS for less motion.
  -->
  <TwnTag
    :rounded="false"
    :severity="pillSeverity"
  >
    <MaterialIcon
      v-if="icon"
      :class="{ 'motion-safe:animate-pulse': animate }"
      :is-filled="isIconFilled"
      :ligature="icon"
      aria-hidden="true"
      size="var(--p-tag-icon-size)"
    />
    <span
      v-else
      :class="{ 'motion-safe:animate-pulse': animate }"
      aria-hidden="true"
      class="size-1.5 shrink-0 rounded-full bg-current"
    />
    <span :class="labelClass">{{ text }}</span>
  </TwnTag>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { IndicatorSeverity, TwnIndicatorProps } from '@/components/atoms/badges/twn-indicator/TwnIndicator.types'

import { pillSeverityFor } from '@/components/atoms/badges/twn-indicator/TwnIndicator.types'
import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'
import TwnTag from '@/components/atoms/prime/tag/twn-tag/TwnTag.vue'
import { activeStateLabel } from '@/types/const/ActiveState.consts'

const props = defineProps<TwnIndicatorProps>()

const severity = computed<IndicatorSeverity>(() => {
  if (props.severity) {
    return props.severity as IndicatorSeverity
  }

  return props.state ? 'success' : 'danger'
})

// A settled state keeps the grey pill.
const pillSeverity = computed(() => pillSeverityFor(severity.value))

const labelClass = computed(() => (props.size === 'small' ? 'text-2xs' : ''))

// The default words are the shared active-state labels, so a bulk "Active State" field offers the same ones.
const text = computed(() => {
  if (props.label) {
    return props.label
  }

  return activeStateLabel(props.state === true)
})
</script>
